import type { ChatAction, Salon } from '../shared/contracts';

export interface PersistedUiMessage {
  id: string;
  role: 'assistant' | 'user';
  text: string;
  actions: ChatAction[];
  failed?: boolean;
  source?: 'ai' | 'human';
  createdAt?: string;
}

export interface PersistedWidgetState {
  sessionId: string;
  visitorToken: string;
  salon: Salon;
  messages: PersistedUiMessage[];
  cursor: number;
  aiPaused: boolean;
}

export function widgetStorageKey(
  apiUrl: string,
  salonSlug: string,
  locale: string,
  market = 'cz',
): string {
  return `blinkit-chat:v3:${encodeURIComponent(apiUrl)}:${encodeURIComponent(
    market,
  )}:${encodeURIComponent(
    salonSlug,
  )}:${encodeURIComponent(locale)}`;
}

export function loadWidgetState(
  key: string,
  storage?: Storage,
): PersistedWidgetState | null {
  try {
    const target = storage || globalThis.localStorage;
    if (!target) return null;
    const value = JSON.parse(target.getItem(key) || 'null') as unknown;
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const state = value as Partial<PersistedWidgetState>;
    if (
      typeof state.sessionId !== 'string' ||
      !state.sessionId ||
      typeof state.visitorToken !== 'string' ||
      !state.visitorToken ||
      !state.salon ||
      typeof state.salon !== 'object' ||
      !Array.isArray(state.messages) ||
      !Number.isInteger(state.cursor) ||
      Number(state.cursor) < 0 ||
      typeof state.aiPaused !== 'boolean'
    ) {
      return null;
    }
    const messages = state.messages
      .filter(
        (message): message is PersistedUiMessage =>
          Boolean(message) &&
          typeof message.id === 'string' &&
          (message.role === 'assistant' || message.role === 'user') &&
          typeof message.text === 'string' &&
          Array.isArray(message.actions),
      )
      .slice(-80);
    if (messages.length === 0) return null;
    return {
      sessionId: state.sessionId,
      visitorToken: state.visitorToken,
      salon: state.salon as Salon,
      messages,
      cursor: Number(state.cursor),
      aiPaused: state.aiPaused,
    };
  } catch {
    return null;
  }
}

export function saveWidgetState(
  key: string,
  state: PersistedWidgetState,
  storage?: Storage,
): void {
  try {
    const target = storage || globalThis.localStorage;
    if (!target) return;
    target.setItem(
      key,
      JSON.stringify({
        ...state,
        messages: state.messages.slice(-80),
      }),
    );
  } catch {
    return;
  }
}

export function clearWidgetState(
  key: string,
  storage?: Storage,
): void {
  try {
    const target = storage || globalThis.localStorage;
    if (!target) return;
    target.removeItem(key);
  } catch {
    return;
  }
}
